import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { 
  RefreshCw, 
  Eye, 
  EyeOff, 
  Shield, 
  CheckCircle, 
  ExternalLink, 
  Key, 
  CreditCard, 
  Mail, 
  AlertTriangle,
  Zap
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const Integrations = () => {
  const { organization } = useOrganization();
  const [openaiApiKey, setOpenaiApiKey] = useState("");
  const [showOpenaiKey, setShowOpenaiKey] = useState(false);
  const [zapierWebhookUrl, setZapierWebhookUrl] = useState("");
  const [contactNotificationEmails, setContactNotificationEmails] = useState("");
  const [paymentNotificationEmails, setPaymentNotificationEmails] = useState("");
  const [stripePublishableKey, setStripePublishableKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);

  useEffect(() => {
    checkAdminAndFetchSettings();
  }, [organization?.id]);

  const checkAdminAndFetchSettings = async () => {
    if (!organization?.id) return;
    
    setFetching(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("Not authenticated");
        return;
      }

      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();

      if (!roles) {
        setIsAdmin(false);
        return;
      }

      setIsAdmin(true);
      await fetchSettings();
    } catch (error: any) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setFetching(false);
    }
  };

  const fetchSettings = async () => {
    if (!organization?.id) return;
    
    try {
      // Fetch org-specific settings from organization_settings
      const { data, error } = await supabase
        .from("organization_settings")
        .select("stripe_publishable_key, openai_api_key, zapier_webhook_url, contact_notification_emails, payment_notification_emails")
        .eq("organization_id", organization.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        setStripePublishableKey(data.stripe_publishable_key || "");
        setOpenaiApiKey(data.openai_api_key || "");
        setZapierWebhookUrl(data.zapier_webhook_url || "");
        setContactNotificationEmails(data.contact_notification_emails || "");
        setPaymentNotificationEmails(data.payment_notification_emails || "");
      }
    } catch (error: any) {
      toast.error(`Failed to fetch settings: ${error.message}`);
    }
  };

  const handleSaveClick = (e: React.FormEvent) => {
    e.preventDefault();
    setShowConfirmDialog(true);
    setConfirmChecked(false);
  };

  const handleConfirmedSave = async () => {
    setShowConfirmDialog(false);
    setLoading(true);

    try {
      const { error } = await supabase
        .from("organization_settings")
        .update({
          stripe_publishable_key: stripePublishableKey || null,
          openai_api_key: openaiApiKey || null,
          zapier_webhook_url: zapierWebhookUrl || null,
          contact_notification_emails: contactNotificationEmails || null,
          payment_notification_emails: paymentNotificationEmails || null,
        })
        .eq("organization_id", organization?.id);

      if (error) throw error;

      toast.success("Integration settings saved successfully");
    } catch (error: any) {
      toast.error(`Save failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const isTestMode = stripePublishableKey?.startsWith("pk_test_");
  const hasAnyOverride = stripePublishableKey || openaiApiKey || zapierWebhookUrl || contactNotificationEmails || paymentNotificationEmails;

  if (fetching) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="p-8">
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-muted-foreground">
            You need admin privileges to access integration settings.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="max-w-3xl">
        <h1 className="text-2xl font-bold font-display mb-2">Integration Settings</h1>
        <p className="text-muted-foreground mb-6">
          Configure integration overrides for your organization
        </p>

        {/* Warning Banner */}
        <Alert className="mb-6 border-amber-500/50 bg-amber-500/10">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-700">Important: Override Warning</AlertTitle>
          <AlertDescription className="text-amber-700/90">
            Changing these settings will override platform-wide defaults for your organization. 
            Incorrect configuration may break payment processing, AI features, or third-party integrations. 
            Leave fields blank to use platform defaults. Changes take effect immediately after saving.
          </AlertDescription>
        </Alert>

        <form onSubmit={handleSaveClick} className="space-y-6">
          {/* Stripe Payments */}
          <Card className="p-6 space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Stripe Payments
                {isTestMode && stripePublishableKey && (
                  <Badge variant="outline" className="ml-2 text-yellow-600 border-yellow-600/30 bg-yellow-500/10">
                    Test Mode
                  </Badge>
                )}
              </h3>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="stripe-pk">
                    Publishable Key Override
                    {!stripePublishableKey && (
                      <span className="ml-2 text-xs text-muted-foreground">(Using platform default)</span>
                    )}
                  </Label>
                  <Input
                    id="stripe-pk"
                    type="text"
                    value={stripePublishableKey}
                    onChange={(e) => setStripePublishableKey(e.target.value)}
                    placeholder="pk_live_... or pk_test_... (leave blank for platform default)"
                  />
                </div>

                <div className="p-4 rounded-lg border border-border bg-muted/30">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-primary" />
                      <Label className="font-medium">Secret Key</Label>
                    </div>
                    <Badge variant="outline" className="gap-1 text-muted-foreground">
                      Platform Managed
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    The Stripe secret key is managed at the platform level for security. 
                    Contact your platform administrator if you need a separate Stripe account.
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Notification Settings */}
          <Card className="p-6 space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Notification Settings
              </h3>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="contact-emails">
                    Contact Form Notifications
                    {!contactNotificationEmails && (
                      <span className="ml-2 text-xs text-muted-foreground">(Using platform default)</span>
                    )}
                  </Label>
                  <Input
                    id="contact-emails"
                    type="text"
                    value={contactNotificationEmails}
                    onChange={(e) => setContactNotificationEmails(e.target.value)}
                    placeholder="email1@example.com, email2@example.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="payment-emails">
                    Payment Notifications
                    {!paymentNotificationEmails && (
                      <span className="ml-2 text-xs text-muted-foreground">(Using platform default)</span>
                    )}
                  </Label>
                  <Input
                    id="payment-emails"
                    type="text"
                    value={paymentNotificationEmails}
                    onChange={(e) => setPaymentNotificationEmails(e.target.value)}
                    placeholder="finance@example.com, admin@example.com"
                  />
                </div>
              </div>
            </div>
          </Card>

          {/* OpenAI API Key */}
          <Card className="p-6 space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Key className="h-5 w-5" />
                OpenAI API
              </h3>
              <div className="space-y-2">
                <Label htmlFor="openai-key">
                  API Key Override
                  {!openaiApiKey && (
                    <span className="ml-2 text-xs text-muted-foreground">(Using platform default)</span>
                  )}
                </Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      id="openai-key"
                      type={showOpenaiKey ? "text" : "password"}
                      value={openaiApiKey}
                      onChange={(e) => setOpenaiApiKey(e.target.value)}
                      placeholder="sk-... (leave blank for platform default)"
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                      onClick={() => setShowOpenaiKey(!showOpenaiKey)}
                    >
                      {showOpenaiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Zapier Integration */}
          <Card className="p-6 space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Zap className="h-5 w-5" />
                Zapier Integration
              </h3>
              
              <div className="mb-4 p-4 rounded-lg border border-border bg-muted/30">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-primary" />
                    <Label className="font-medium">Zapier Authentication Secret</Label>
                  </div>
                  <Badge variant="outline" className="gap-1 text-muted-foreground">
                    Platform Managed
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  The ZAPIER_SECRET is managed at the platform level for security.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="zapier-webhook">
                  Registration Webhook URL
                  {!zapierWebhookUrl && (
                    <span className="ml-2 text-xs text-muted-foreground">(Using platform default)</span>
                  )}
                </Label>
                <Input
                  id="zapier-webhook"
                  type="url"
                  value={zapierWebhookUrl}
                  onChange={(e) => setZapierWebhookUrl(e.target.value)}
                  placeholder="https://hooks.zapier.com/hooks/catch/..."
                />
              </div>
            </div>
          </Card>

          <div className="flex justify-end">
            <Button type="submit" disabled={loading}>
              {loading ? "Saving..." : "Save Settings"}
            </Button>
          </div>
        </form>

        {/* Confirmation Dialog */}
        <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                Confirm Integration Changes
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-4">
                <p>
                  You are about to save integration settings that will override platform-wide defaults 
                  for your organization.
                </p>
                <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <Checkbox 
                    id="confirm-understand"
                    checked={confirmChecked}
                    onCheckedChange={(checked) => setConfirmChecked(checked === true)}
                  />
                  <label htmlFor="confirm-understand" className="text-sm leading-relaxed cursor-pointer">
                    I understand that these changes will override platform-wide defaults and may affect 
                    my organization's payment processing, AI features, and third-party integrations.
                  </label>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleConfirmedSave}
                disabled={!confirmChecked}
                className="bg-amber-600 hover:bg-amber-700"
              >
                Save Changes
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default Integrations;
